import { useState, useRef } from "react";
import { F, D } from "../../../styles/palette";
import { DC } from "../../shared";

const REGIONES = [
  "Miembro superior derecho",
  "Miembro superior izquierdo",
  "Miembro inferior derecho",
  "Miembro inferior izquierdo",
  "Mano derecha",
  "Mano izquierda",
  "Pie derecho",
  "Pie izquierdo",
  "Tronco / Tórax",
  "Abdomen / Pelvis",
  "Cabeza / Cuello",
  "Genital",
  "Otra región",
];

export default function PhotoUpload({ user, patientId, recordId, uploadPhoto, onClose, onUploaded }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [region, setRegion] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [success, setSuccess] = useState(false);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      setError("Solo se permiten archivos de imagen");
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      setError("El archivo no debe superar 10MB");
      return;
    }
    setError(null);
    setFile(f);
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target.result);
    reader.readAsDataURL(f);
  };

  const handleUpload = async () => {
    if (!file) { setError("Seleccione una imagen"); return; }
    if (!region) { setError("Seleccione la región corporal"); return; }
    setError(null);
    setUploading(true);
    try {
      await uploadPhoto(file, { regionCorporal: region, descripcion, recordId, user });
      setSuccess(true);
      setTimeout(() => {
        onUploaded?.();
        onClose?.();
      }, 1000);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const clearFile = () => {
    setFile(null);
    setPreview(null);
    if (fileRef.current) fileRef.current.value = "";
    if (cameraRef.current) cameraRef.current.value = "";
  };

  if (success) {
    return (
      <DC>
        <div style={{ textAlign: "center", padding: 30 }}>
          <div style={{ fontSize: 40, marginBottom: 10 }}>&#9989;</div>
          <h3 style={{ color: "#4ade80", fontSize: 16, margin: 0, fontFamily: F.b }}>Foto subida exitosamente</h3>
        </div>
      </DC>
    );
  }

  return (
    <div>
      <button onClick={onClose} style={{ background: "none", border: "none", color: D.ac, cursor: "pointer", fontSize: 13, fontFamily: F.b, marginBottom: 12, padding: 0 }}>
        &larr; Volver
      </button>
      <h3 style={{ color: "#e0f2fe", fontSize: 17, margin: "0 0 14px", fontFamily: F.dm }}>Subir Fotografía Clínica</h3>

      {error && <p style={{ color: "#f87171", fontSize: 13, marginBottom: 10 }}>{error}</p>}

      {/* Capture buttons */}
      {!preview && (
        <DC title="Captura" accent="#0284c7">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <input ref={cameraRef} type="file" accept="image/*" capture="environment" onChange={handleFile} style={{ display: "none" }} />
              <button onClick={() => cameraRef.current?.click()}
                style={{ width: "100%", padding: "20px 14px", borderRadius: 10, border: `2px dashed rgba(2,132,199,0.4)`, background: "rgba(2,132,199,0.05)", color: "#0284c7", cursor: "pointer", textAlign: "center", fontFamily: F.b }}>
                <div style={{ fontSize: 28, marginBottom: 6 }}>&#128247;</div>
                <div style={{ fontSize: 13, fontWeight: 700 }}>Cámara</div>
                <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>Tomar foto</div>
              </button>
            </div>
            <div>
              <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} style={{ display: "none" }} />
              <button onClick={() => fileRef.current?.click()}
                style={{ width: "100%", padding: "20px 14px", borderRadius: 10, border: `2px dashed rgba(100,116,139,0.3)`, background: "rgba(30,41,59,0.5)", color: "#94a3b8", cursor: "pointer", textAlign: "center", fontFamily: F.b }}>
                <div style={{ fontSize: 28, marginBottom: 6 }}>&#128193;</div>
                <div style={{ fontSize: 13, fontWeight: 700 }}>Archivo</div>
                <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>Seleccionar imagen</div>
              </button>
            </div>
          </div>
        </DC>
      )}

      {/* Preview */}
      {preview && (
        <DC title="Vista previa" accent="#0284c7">
          <div style={{ position: "relative", marginBottom: 10, textAlign: "center" }}>
            <img src={preview} alt="Preview" style={{ maxWidth: "100%", maxHeight: 300, borderRadius: 8, border: `1px solid ${D.bdr}` }} />
            <button onClick={clearFile}
              style={{ position: "absolute", top: 6, right: 6, width: 28, height: 28, borderRadius: "50%", border: "none", background: "rgba(0,0,0,0.7)", color: "#fff", fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
              &times;
            </button>
          </div>
          {file && <p style={{ color: "#64748b", fontSize: 11, margin: 0, fontFamily: F.m }}>{file.name} — {(file.size / 1024).toFixed(0)} KB (se comprimirá automáticamente)</p>}
        </DC>
      )}

      {/* Región corporal */}
      <DC title="Región corporal" accent="#dc2626">
        <label style={{ color: "#94a3b8", fontSize: 12, display: "block", marginBottom: 8, fontFamily: F.b }}>
          Seleccione la región <span style={{ color: "#f87171" }}>*</span>
        </label>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
          {REGIONES.map(r => {
            const active = region === r;
            return (
              <button key={r} onClick={() => setRegion(r)}
                style={{ padding: "8px 10px", borderRadius: 7, border: `1px solid ${active ? "#dc2626" : "rgba(100,116,139,0.12)"}`, background: active ? "rgba(220,38,38,0.1)" : "transparent", color: active ? "#fca5a5" : "#94a3b8", fontSize: 12, cursor: "pointer", textAlign: "left", fontFamily: F.b }}>
                {r}
              </button>
            );
          })}
        </div>
      </DC>

      {/* Descripción */}
      <DC title="Descripción" accent="#a78bfa">
        <textarea value={descripcion} onChange={e => setDescripcion(e.target.value)} rows={2}
          style={{ width: "100%", padding: "10px 14px", borderRadius: 8, border: "1px solid rgba(100,116,139,0.2)", background: "rgba(30,41,59,0.8)", color: "#e2e8f0", fontSize: 13, fontFamily: F.b, boxSizing: "border-box", resize: "vertical" }}
          placeholder="Descripción opcional de la imagen (ej: vista anterior, post-tratamiento)..." />
      </DC>

      {/* Buttons */}
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 8 }}>
        <button onClick={onClose}
          style={{ padding: "10px 22px", borderRadius: 8, border: `1px solid ${D.bdr}`, background: "transparent", color: "#94a3b8", fontSize: 13, cursor: "pointer", fontFamily: F.b }}>
          Cancelar
        </button>
        <button onClick={handleUpload} disabled={uploading || !file || !region}
          style={{ padding: "10px 22px", borderRadius: 8, border: "none", background: uploading || !file || !region ? "#475569" : "#0284c7", color: "#fff", fontSize: 13, fontWeight: 700, cursor: uploading ? "default" : "pointer", fontFamily: F.b }}>
          {uploading ? "Subiendo..." : "Subir Fotografía"}
        </button>
      </div>
    </div>
  );
}
